import { useState, useEffect } from 'react';
import Price from '../Price';
import Volume from '../Volume';
import {
  Stack,
  Typography,
  Paper,
  Box,
  Checkbox,
  Divider,
  NumberInput,
  Radio,
  FormControlLabel,
  Switch,
} from '@newulife/common';
import formatImageSrc from 'helpers/formatImageSrc';

export default function ProductCard({
  useNewDesign = false,
  subscribeText,
  initialOrderText,
  subscribeSaveText,
  addToTodaysOrderText,
  imgSrc,
  imgAlt,
  title,
  cv,
  qv,
  price,
  autoShipPrice,
  qty,
  maxQty,
  decrement,
  increment,
  disableSubscribe,
  isSubscribed,
  isAddedToInitialOrder,
  toggleSubscription,
  toggleAdditionToInitialOrder,
  inModal = false,
}) {
  const setMode = ({ subscribe, addToOrder, isFromSubscribeCard = false }) => {
    if (subscribe === isSubscribed && addToOrder === isAddedToInitialOrder) return;

    if (subscribe && !isSubscribed) {
      toggleSubscription(); // turns on subscribe
    } else if (!subscribe && isSubscribed) {
      toggleSubscription(); // turns off subscribe
    }

    if (addToOrder && !isAddedToInitialOrder) {
      toggleAdditionToInitialOrder(isFromSubscribeCard); // turn on today's order
    } else if (!addToOrder && isAddedToInitialOrder) {
      toggleAdditionToInitialOrder(isFromSubscribeCard); // turn off today's order
    }
  };

  return !useNewDesign ? (
    <Paper
      elevationVariant="card"
      active={qty > 0}
      data-testid={`${title?.replace(/\W/g, '')}-card`}
      sx={{
        display: 'inline-grid',
        width: '100%',
        height: '100%',
      }}
      className="product-card"
    >
      <Stack
        display="grid"
        p={{
          xs: 1.5,
          md: 2,
        }}
        pb={0}
        height="100%"
        width="100%"
        maxWidth="340px"
        justifySelf="center"
      >
        <Stack
          display="grid"
          gap={{
            md: 3,
          }}
          gridTemplateColumns={{
            md: 'auto 1fr',
          }}
          justifyItems={{
            xs: 'center',
            md: 'initial',
          }}
          height="100%"
          mb={2}
        >
          <Box
            width="120px"
            height="120px"
            sx={{
              img: {
                width: '100%',
                height: 'auto',
              },
            }}
          >
            <img src={formatImageSrc(imgSrc)} alt={imgAlt} />
          </Box>
          <Stack gap={1}>
            <Typography variant="body2" textAlign="center">
              {title}
            </Typography>
            <Divider light />
            {cv && qv && (
              <>
                <Stack color="#747679" gap={0.5}>
                  <Typography variant="subbody2">
                    <Volume type="cv" value={cv} />
                  </Typography>
                  <Typography variant="subbody2">
                    <Volume type="qv" value={qv} />
                  </Typography>
                </Stack>
                <Divider light />
              </>
            )}
            <Typography
              data-testid={`${title?.replace(/\W/g, '')}-price`}
              variant="header2"
              color="neutralCool.dark"
              sx={{
                fontWeight: 500,
              }}
            >
              <Price value={price} />
            </Typography>
          </Stack>
        </Stack>
        <Divider
          sx={{
            opacity: 0.6,
            mb: 2,
          }}
        />
        <Stack direction="row" justifyContent="space-between">
          <NumberInput
            data-testid={`${title?.replace(/\W/g, '')}-footer`}
            min={0}
            max={maxQty}
            value={qty}
            onChange={(data) => {
              data > qty ? increment() : decrement();
            }}
          />
          <FormControlLabel
            disabled={disableSubscribe}
            control={<Switch data-testid="subscribe-checkbox" checked={isSubscribed} onChange={toggleSubscription} />}
            label={
              <Typography variant="subbody2" color="neutralCool.dark">
                {subscribeText}
              </Typography>
            }
            sx={{
              ':has(.Mui-disabled)': {
                opacity: 0.4,
              },
            }}
          />
        </Stack>
        {isSubscribed && !disableSubscribe && (
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch checked={isAddedToInitialOrder} onChange={toggleAdditionToInitialOrder} />
              }
              label={
                <Typography variant="subbody2" color="neutralCool.dark">
                  {initialOrderText}
                </Typography>
              }
              sx={{
                ':has(.Mui-disabled)': {
                  opacity: 0.4,
                },
              }}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  ) : (
    <Paper
      elevationVariant="card"
      active={isSubscribed || isAddedToInitialOrder}
      borderColorActive="primary"
      data-testid={`${title?.replace(/\W/g, '')}-card`}
      sx={{
        display: 'inline-grid',
        width: '100%',
        height: '100%',
      }}
      className="product-card"
    >
      <Stack
        display="grid"
        gridTemplateRows="auto 1fr"
        p={{
          xs: 1.5,
          md: 2,
        }}
        pb={0}
        height="100%"
        width="100%"
        maxWidth="340px"
        justifySelf="center"
      >
        <Stack
          display="grid"
          gap={{
            md: 3,
          }}
          gridTemplateColumns={{
            md: 'auto 1fr',
          }}
          justifyItems={{
            xs: 'center',
            md: 'initial',
          }}
          height="100%"
          mb={2}
        >
          <Box
            width="120px"
            height="120px"
            sx={{
              img: {
                width: '100%',
                height: 'auto',
              },
            }}
          >
            <img src={formatImageSrc(imgSrc)} alt={imgAlt} />
          </Box>
          <Stack gap={1}>
            <Typography variant="body2" textAlign="left">
              {title}
            </Typography>
            <Divider sx={{ opacity: 0.6 }} />
            {cv && qv && (
              <>
                <Stack color="#747679" gap={0.5}>
                  <Typography variant="subbody2">
                    <Volume type="cv" value={cv} />
                  </Typography>
                  <Typography variant="subbody2">
                    <Volume type="qv" value={qv} />
                  </Typography>
                </Stack>
                <Divider sx={{ opacity: 0.6 }} />
              </>
            )}
            <Typography
              data-testid={`${title?.replace(/\W/g, '')}-price`}
              variant="header2"
              color="neutralCool.dark"
              sx={{
                fontWeight: 500,
              }}
            >
              <Price value={price} />
            </Typography>
          </Stack>
        </Stack>

        {/* Selection Options Section */}
        <Stack gap={1.25}>
          {/* Add to Today's Order Option */}
          {!disableSubscribe && (
            <Box
              onClick={(e) => {
                if (!(isAddedToInitialOrder && !isSubscribed))
                  setMode({ subscribe: false, addToOrder: true });
              }}
              sx={{
                backgroundColor: isAddedToInitialOrder && !isSubscribed ? '#e6edff' : '#f0f0f0',
                borderRadius: 2,
                p: 1.5,
                transition: 'background-color 0.3s ease',
                cursor: isAddedToInitialOrder && !isSubscribed ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
              }}
            >
              {/* Radio Button */}
              <Checkbox
                data-testid="add-to-order-checkbox"
                checked={isAddedToInitialOrder && !isSubscribed}
                onClick={(e) => {
                  e.stopPropagation();
                  setMode({ subscribe: false, addToOrder: !isAddedToInitialOrder });
                }}
                sx={{
                  color: '#012169',
                  '&.Mui-checked': {
                    color: '#012169',
                  },
                }}
              />

              {/* Content */}
              <Stack gap={1.25} sx={{ flex: 1 }}>
                <Typography variant="body1" color="neutralCool.dark">
                  {addToTodaysOrderText}
                </Typography>
                {isAddedToInitialOrder && !isSubscribed && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <NumberInput
                      min={0}
                      max={maxQty}
                      value={qty}
                      onChange={(data) => {
                        data > qty ? increment() : decrement();
                        if (data === 0) {
                          setMode({ subscribe: false, addToOrder: false });
                        }
                      }}
                      IconButtonProps={{
                        sx: {
                          color: '#012169',
                          '&:active, &:focus, &:hover': {
                            backgroundColor: '#fff',
                            color: '#012169',
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          {/* Subscribe & Save Option */}
          <Box
            onClick={(e) => {
              if (!isSubscribed) setMode({ subscribe: true, addToOrder: false });
            }}
            sx={{
              backgroundColor: isSubscribed ? '#e6edff' : '#f0f0f0',
              borderRadius: 2,
              p: 1.5,
              transition: 'background-color 0.3s ease',
              cursor: isSubscribed ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
            }}
          >
            {/* Radio Button */}
            <Checkbox
              data-testid="subscribe-checkbox"
              checked={isSubscribed}
              onClick={(e) => {
                e.stopPropagation();
                setMode({ subscribe: !isSubscribed, addToOrder: false });
              }}
              sx={{
                color: '#012169',
                '&.Mui-checked': {
                  color: '#012169',
                },
              }}
            />

            {/* Content */}
            <Stack gap={1.25} sx={{ flex: 1 }}>
              <Stack gap={0.5}>
                <Typography variant="body1" color="neutralCool.dark">
                  {autoShipPrice ? subscribeSaveText : subscribeText}
                </Typography>
                {autoShipPrice && (
                  <Typography variant="subhead2" color="neutralCool.dark">
                    <Price value={autoShipPrice.Price} />
                  </Typography>
                )}
              </Stack>
              {isSubscribed && (
                <>
                  <NumberInput
                    min={0}
                    max={maxQty}
                    value={qty}
                    onChange={(data) => {
                      data > qty ? increment() : decrement();
                      if (data === 0) {
                        setMode({ subscribe: false, addToOrder: false });
                      }
                    }}
                    IconButtonProps={{
                      sx: {
                        color: '#012169',
                        '&:active, &:focus, &:hover': {
                          backgroundColor: '#fff',
                          color: '#012169',
                        },
                      },
                    }}
                  />

                  {/* Also add to today's order checkbox */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Checkbox
                      data-testid="add-to-order-with-subscription-checkbox"
                      size="small"
                      checked={isAddedToInitialOrder}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMode({
                          subscribe: true,
                          addToOrder: !isAddedToInitialOrder,
                          isFromSubscribeCard: true,
                        });
                      }}
                      sx={{
                        color: '#012169',
                        '&.Mui-checked': {
                          color: '#012169',
                        },
                      }}
                    />
                    <Typography variant="subbody2" color="neutralCool.dark">
                      {initialOrderText}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}
